import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { selectPermissionsForUser } from '@authorisation/selectors';
import { determineSourceTypeForNewContent } from '@utils';
import ContentRef from '@utils/contentRef';
import useContentRefs from '@hooks/useContentRefs';
import { editVideo } from '@redux/documents/actions';
import { selectDocumentEditing } from '@redux/documents/reducers';
import { selectLanguage } from '@redux/core/reducers';
import { languageFromMetadata } from '@utils/contentMetadata';
import { usePageActions } from '@hooks/usePageActions';
import { matchType } from '../../../propTypes';
import VideoWizard from './index';
import getVideoId from './getVideoId';

const nowMoment = moment();

function EditVideo({ match }) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  // since video is part of document, document data object is used here.

  const contentRef = ContentRef.fromUrlFragment(match.params.id);
  const { [contentRef]: document } = useContentRefs([contentRef]);
  const loading = document === undefined;

  const permissions = useSelector(selectPermissionsForUser);
  const editing = useSelector(selectDocumentEditing);
  const findLanguage = useSelector(languageFromMetadata);
  const language = useSelector(selectLanguage(findLanguage(document)));
  const [initialData, setInitialData] = useState({
    content: {
      title: undefined,
      videoDescription: undefined,
      link: undefined,
      videoType: undefined,
    },
    details: {
      name: undefined,
      description: undefined,
      language: undefined,
    },
  });

  const { setTitle } = usePageActions({
    title: '',
    showClose: true,
  });

  useEffect(() => {
    if (document) {
      setTitle(`${t('videos:Wizard.edit')} ${document.name}`);

      const customContent = document.metadata?.custom?.content;
      const videoType = customContent?.video_type || '';

      let link = '';
      if (customContent?.video_url && videoType !== 'youtube') link = customContent?.video_url;
      if (customContent?.video_id) link = `https://youtu.be/${customContent?.video_id}`;

      setInitialData({
        content: {
          title: customContent?.heading || '',
          videoDescription: customContent?.description || '',
          link,
          publishDate: customContent?.publishDate || '',
          runTime: customContent?.runTime || '',
          videoType,
        },
        details: {
          name: document.name,
          description: document.description,
          language: language?.key,
          latestVersion: document.latest_version.name,
          audience: document.audience_type,
        },
      });
    }
  }, [document, language, setTitle, t]);

  const onCompleted = useCallback(
    formData => {
      if (formData) {
        const { content, details } = formData;

        const editedVideo = {
          description: details.description,
          latest_version: { created: nowMoment.toISOString(), name: details.name },
          published: details.status === 'published',
          name: details.name,
          metadata: {
            language: details.language || 'en',
            source: determineSourceTypeForNewContent(permissions),
            audience: details.audience,
          },
          videoData: {
            videoType: content.videoType,
            videoTitle: content.title,
            videoDescription: content.videoDescription,
            videoId: getVideoId(content.link),
            videoURL: content.link,
            publishDate: content.publishDate,
            runTime: content.runTime,
          },
        };

        dispatch(
          editVideo(
            document.latest_version?.metadata?.readonly?.ls?.carbon?.uuid,
            document.id,
            editedVideo,
          ),
        );
      }
    },
    [dispatch, document, permissions],
  );

  return (
    <VideoWizard
      loading={loading}
      submitting={editing}
      initialData={initialData}
      onCompleted={onCompleted}
      isEdit={true}
    />
  );
}

EditVideo.propTypes = {
  match: matchType.isRequired,
};

export default EditVideo;
